<template>
  <div
    class="py-4 bg-box-container-bg dark:bg-box-container-bg-dark border-r border-container-border dark:border-container-border-dark flex flex-col transition-[width,min-width] duration-200 ease-in-out motion-reduce:transition-none"
    :class="collapsed ? 'w-16 min-w-16' : 'w-64 min-w-64'"
  >
    <!-- Only the navigation scrolls: the footer stays pinned, and its
         settings menu can overflow the sidebar (to the right of the rail). -->
    <div
      class="flex-1 min-h-0 px-3 overflow-y-auto overflow-x-hidden"
      :class="collapsed ? 'space-y-2' : 'space-y-4'"
    >
      <div
        v-for="(category, categoryKey, categoryIndex) in categorizedRoutes"
        :key="categoryKey"
        class="flex flex-col"
      >
        <!-- Rail: section headers don't fit, so a divider separates sections
             and every item is shown regardless of the remembered state. -->
        <div
          v-if="collapsed && categoryIndex > 0"
          aria-hidden="true"
          class="mx-2 mb-2 border-t border-container-border dark:border-container-border-dark"
        ></div>
        <button
          v-else-if="
            !collapsed && category.name && !isStandaloneCategory(category)
          "
          type="button"
          @click="toggleCategory(categoryKey)"
          :aria-expanded="isCategoryOpen(categoryKey)"
          class="flex items-center justify-between w-full text-[11px] uppercase tracking-wider font-semibold text-muted-text dark:text-muted-text-dark hover:text-heading dark:hover:text-heading-dark px-2.5 py-1 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
        >
          <span>{{ t(`partials.sidebar.${category.name}`) }}</span>
          <component
            :is="MdiChevronDown"
            class="w-4 h-4 transition-transform duration-200"
            :class="{ 'rotate-180': isCategoryOpen(categoryKey) }"
            aria-hidden="true"
          />
        </button>

        <div
          v-show="
            collapsed ||
            isStandaloneCategory(category) ||
            isCategoryOpen(categoryKey)
          "
          :role="collapsed ? 'group' : undefined"
          :aria-label="
            collapsed && category.name
              ? t(`partials.sidebar.${category.name}`)
              : undefined
          "
          class="space-y-0.5"
          :class="{ 'mt-1': !collapsed }"
        >
          <SidebarTooltip
            v-for="route in category.routes"
            :key="route.path"
            :text="routeTooltip(route.name, route.path)"
            :disabled="!collapsed"
          >
            <router-link :to="route.path" custom v-slot="{ href, navigate }">
              <MenuButton
                :href="href"
                :data-tour="`nav:${route.path}`"
                @click="
                  () => {
                    if (isFeatureEnabled(route.path)) {
                      navigate();
                      emit('navigate');
                    }
                  }
                "
                :icon="route.icon"
                :label="t(`partials.sidebar.${route.name}`)"
                :active="currentRoute.path.startsWith(route.path)"
                :collapsed="collapsed"
                :aria-disabled="!isFeatureEnabled(route.path) || undefined"
                :class="{
                  'opacity-60 cursor-not-allowed': !isFeatureEnabled(
                    route.path,
                  ),
                }"
              />
            </router-link>
          </SidebarTooltip>
        </div>
      </div>
    </div>

    <div
      class="mt-3 pt-3 px-3 space-y-0.5 border-t border-container-border dark:border-container-border-dark"
    >
      <SidebarTooltip
        v-if="collapsible"
        :text="collapseLabel"
        :disabled="!collapsed"
      >
        <MenuButton
          :icon="collapsed ? MdiChevronDoubleRight : MdiChevronDoubleLeft"
          :label="collapseLabel"
          :collapsed="collapsed"
          @click="emit('toggle-collapse')"
        />
      </SidebarTooltip>

      <div
        class="relative"
        ref="settingsContainerRef"
        @keydown.escape="closeSettingsMenu"
      >
        <SidebarTooltip
          :text="userName"
          :disabled="!collapsed || isSettingsMenuOpen"
        >
          <MenuButton
            ref="settingsTriggerRef"
            data-tour="user-menu"
            :icon="MdiCogOutline"
            :label="userName"
            :collapsed="collapsed"
            @click="toggleSettingsMenu"
            type="button"
            :aria-expanded="isSettingsMenuOpen"
            aria-haspopup="true"
            :active="
              isSettingsMenuOpen || currentRoute.path === '/account/settings'
            "
          >
            <!-- Rail: the avatar stands in for the cog, which would read as
                 a duplicate of Organization > Configuration. -->
            <template v-if="collapsed" #icon>
              <Avatar :name="user.firstName" />
            </template>
            <template #right-element>
              <Avatar :name="user.firstName" />
            </template>
          </MenuButton>
        </SidebarTooltip>

        <UserSettingsMenu
          v-if="isSettingsMenuOpen"
          class="absolute z-20"
          :class="
            collapsed
              ? 'bottom-0 left-[calc(100%+20px)]'
              : 'bottom-[calc(100%+10px)]'
          "
          :user-name="userName"
          :user-org="selectedMembership.organizationName"
          :role="effectiveRole"
          @logout="handleLogout"
          @account="handleAccount"
          @tour="handleTour"
        />
      </div>
    </div>

    <!-- Logging out asks first: the menu opens above its trigger, so a
         second click on it can land on "Logout". -->
    <Modal
      v-model="showLogoutConfirm"
      size="small"
      :title="t('userSettingsMenu.logoutConfirm.title')"
      :message="t('userSettingsMenu.logoutConfirm.message')"
      :confirm-button-text="t('userSettingsMenu.logoutConfirm.confirm')"
      confirm-variant="primary"
      @confirm="confirmLogout"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  onMounted,
  onUnmounted,
  type Component,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import MdiCogOutline from "~icons/mdi/cog-outline";
import MdiChevronDown from "~icons/mdi/chevron-down";
import MdiChevronDoubleLeft from "~icons/mdi/chevron-double-left";
import MdiChevronDoubleRight from "~icons/mdi/chevron-double-right";
import { useAuth } from "./../composables/use-auth";
import { useDashboardTour } from "../composables/use-dashboard-tour";
import MenuButton from "../components/MenuButton.vue";
import SidebarTooltip from "../components/SidebarTooltip.vue";
import UserSettingsMenu from "../components/UserSettingsMenu.vue";
import Modal from "../components/Base/Modal.vue";
import Avatar from "../components/Avatar.vue";
import { isFeatureEnabled } from "../utils/feature-flags";
import { routes } from "../main";
import { Role } from "tf-api-client";
import { useOrg } from "../composables/use-org";

const { t } = useI18n({ useScope: "global" });
const { selectedMembership, effectiveRole, user, logout } = useAuth();
const { isManagerOrg } = useOrg();
const { startTour } = useDashboardTour();
const router = useRouter();
const currentRoute = useRoute();
const userRole = computed(() => effectiveRole.value);

const props = withDefaults(
  defineProps<{
    // Icon-only rail (desktop): labels move to tooltips.
    collapsed?: boolean;
    // Shows the collapse/expand toggle; off for the mobile drawer.
    collapsible?: boolean;
  }>(),
  { collapsed: false, collapsible: false },
);

const emit = defineEmits<{
  (e: "navigate"): void;
  (e: "toggle-collapse"): void;
}>();

const userName = computed(
  () => `${user.value.firstName} ${user.value.lastName}`,
);

const collapseLabel = computed(() =>
  t(props.collapsed ? "partials.sidebar.expand" : "partials.sidebar.collapse"),
);

const routeTooltip = (name: string, path: string) => {
  const label = t(`partials.sidebar.${name}`);
  return isFeatureEnabled(path)
    ? label
    : `${label} · ${t("partials.sidebar.unavailable")}`;
};

const isSettingsMenuOpen = ref(false);
const settingsContainerRef = ref<HTMLDivElement | null>(null);
const settingsTriggerRef = ref<InstanceType<typeof MenuButton> | null>(null);

// Escape closes the settings menu and hands focus back to its trigger.
const closeSettingsMenu = () => {
  if (!isSettingsMenuOpen.value) return;
  isSettingsMenuOpen.value = false;
  (settingsTriggerRef.value?.$el as HTMLElement | undefined)?.focus();
};

const openCategories = ref<Record<string, boolean>>({});
const OPEN_CATEGORIES_STORAGE_KEY = "sidebar-open-categories";

// A category whose only route carries the category's own name (e.g.
// "Dashboard > Dashboard") renders as a single top-level item instead of a
// redundant header + identical child.
const isStandaloneCategory = (category: {
  name: string | undefined;
  routes: Array<{ name: string }>;
}) => category.routes.length === 1 && category.routes[0].name === category.name;

// Sections are expanded unless the user collapsed them: only an explicit
// `false` is remembered as closed. Defaulting to closed would hide every item
// of a newly added section (e.g. "Tools") until the user found and opened it.
const isCategoryOpen = (key: string | number) =>
  openCategories.value[key] !== false;

const toggleCategory = (key: string | number) => {
  openCategories.value[key] = !isCategoryOpen(key);
};

const loadOpenCategories = () => {
  try {
    const rawValue = localStorage.getItem(OPEN_CATEGORIES_STORAGE_KEY);

    if (!rawValue) {
      return;
    }

    const parsedValue = JSON.parse(rawValue);

    if (parsedValue && typeof parsedValue === "object") {
      openCategories.value = parsedValue as Record<string, boolean>;
    }
  } catch {
    // Ignore malformed localStorage data and fall back to defaults.
  }
};

const saveOpenCategories = () => {
  try {
    localStorage.setItem(
      OPEN_CATEGORIES_STORAGE_KEY,
      JSON.stringify(openCategories.value),
    );
  } catch {
    // Ignore write errors (e.g. storage disabled or quota exceeded).
  }
};

const expandActiveCategory = () => {
  for (const [key, category] of Object.entries(categorizedRoutes.value)) {
    const hasActiveChild = category.routes.some((route) =>
      currentRoute.path.startsWith(route.path),
    );

    if (hasActiveChild && openCategories.value[key] === undefined) {
      openCategories.value[key] = true;
    }
  }
};

const toggleSettingsMenu = () => {
  isSettingsMenuOpen.value = !isSettingsMenuOpen.value;
};

const handleClickOutside = (event: MouseEvent) => {
  if (
    settingsContainerRef.value &&
    !settingsContainerRef.value.contains(event.target as Node)
  ) {
    isSettingsMenuOpen.value = false;
  }
};

onMounted(() => {
  loadOpenCategories();
  expandActiveCategory();
  document.addEventListener("mousedown", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("mousedown", handleClickOutside);
});

const showLogoutConfirm = ref(false);

const handleLogout = () => {
  isSettingsMenuOpen.value = false;
  showLogoutConfirm.value = true;
};

const confirmLogout = () => {
  emit("navigate");
  logout();
  router.push("/login");
};

const handleAccount = () => {
  isSettingsMenuOpen.value = false;
  emit("navigate");
  router.push("/account/settings");
};

const handleTour = () => {
  isSettingsMenuOpen.value = false;
  emit("navigate");
  startTour();
};

// Sidebar sections, in display order. A route is grouped by its
// `meta.section` when set, otherwise by its first path segment — so a page
// can be shown under a different section (e.g. the event logs under
// "Monitoring") without changing its URL, which feature flags and bookmarks
// depend on. Sections not listed here follow, in route order.
const SECTION_ORDER = [
  "dashboard",
  "monitoring",
  "protection",
  "e-learning",
  "phishing",
  "organization",
];

const categorizedRoutes = computed(() => {
  const baseRoutes: {
    [key: string]: {
      name: string | undefined;
      routes: Array<{
        name: string;
        path: string;
        icon: Component;
      }>;
    };
  } = {};

  for (let route of routes[0]?.children || []) {
    const roles = route.meta?.roles as Role[];

    if (roles && !roles.includes(userRole.value)) {
      continue;
    }

    if (route.meta?.requiresManagerOrg && !isManagerOrg.value) {
      continue;
    }

    const hasIcon = route.meta?.icon;

    if (hasIcon) {
      const parts = route.path.split("/");
      const category =
        (route.meta?.section as string | undefined) || parts[1] || "general";
      const name = parts[parts.length - 1] || "unknown";
      if (!baseRoutes[category]) {
        baseRoutes[category] = {
          name: category,
          routes: [],
        };
      }
      baseRoutes[category].routes.push({
        name: name,
        path: route.path,
        icon: route.meta!.icon as Component,
      });
    }
  }

  const rank = (category: string) => {
    const index = SECTION_ORDER.indexOf(category);
    return index === -1 ? SECTION_ORDER.length : index;
  };
  // Rebuilt in display order (string keys keep insertion order).
  return Object.fromEntries(
    Object.entries(baseRoutes).sort(([a], [b]) => rank(a) - rank(b)),
  );
});

watch(
  () => currentRoute.path,
  () => expandActiveCategory(),
  { immediate: true },
);

watch(openCategories, () => saveOpenCategories(), { deep: true });
</script>
